import { ConnectedProps, connect } from 'react-redux';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import {
    ActionButton,
    IChoiceGroupOption,
    Icon,
    IconButton,
    MessageBar,
    MessageBarType,
    Stack,
    TooltipHost,
} from '@fluentui/react';
import {
    ChoiceGroup,
    Label,
    LoadingSpinner,
    SearchComboBox,
    SearchCriteria,
} from '@eta/components';
import { useTranslation } from 'react-i18next';
import { ReduxState } from '../../../common/reducers';
import './AccessBranches.scss';
import { GetTaxpayerBranchDto } from '../../../Services/ClientProxy/clientProxy';
import { BranchSearchItem } from './BranchSearchItem';
import { RepresentationContext } from './Context/RepresentationContext';
import { InviteUserMode, inviteUserWizardKey } from './InviteUserMain';
import { WizardStateAndLogic } from '../../../common/component/WizardPivot';
import { loadTaxpayerProfile } from '../../Redux/Actions';

const mapDispatchToProps = {
    dispatchLoadTaxpayerProfile: loadTaxpayerProfile,
};

const mapStateToProps = (state: ReduxState) => ({
    currentLanguage: state.reduxlanguage.language,
    taxpayerBranchs:
        state.taxpayerProfile.currentTaxpayerProfile?.taxpayerBranchs,
    lastError: state.taxpayerProfile.lastError,
    currentTaxpayerProfile: state.taxpayerProfile.currentTaxpayerProfile,
});

export enum BranchAccess {
    All = 'All',
    Custom = 'Custom',
}

export interface AccessBranchesProps {
    readOnly?: boolean;
    mode?: InviteUserMode;
    InviteUserWizard?: WizardStateAndLogic;
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const AccessBranches: FC<AccessBranchesProps & PropsFromRedux> = (
    props: AccessBranchesProps & PropsFromRedux
) => {
    const { t } = useTranslation(['InviteUser']);
    const {
        currentLanguage,
        taxpayerBranchs,
        readOnly,
        mode,
        InviteUserWizard,
        lastError,
        currentTaxpayerProfile,
        dispatchLoadTaxpayerProfile,
    } = props;
    const { state: currentInvitation, dispatch: dispatchInvitationRequest } =
        useContext(RepresentationContext);

    const nbItemPerPage = 10;
    const notRestrictedBranch = currentInvitation.restrictedBranch
        ? taxpayerBranchs?.filter(
              (x) =>
                  !currentInvitation?.restrictedBranch?.includes(
                      x.branchNumber ?? -1
                  )
          ) ?? []
        : taxpayerBranchs;

    const [selectedKey, setSelectedKey] = useState<BranchAccess>(
        currentInvitation.allBranches ? BranchAccess.All : BranchAccess.Custom
    );
    const [selectedBranches, setSelectedBranches] = useState<
        GetTaxpayerBranchDto[]
    >([]);

    const [searchBranches, setSearchBranches] = useState<
        GetTaxpayerBranchDto[]
    >(notRestrictedBranch ?? []);
    const allBranches = useRef<GetTaxpayerBranchDto[]>(
        notRestrictedBranch ?? []
    );
    const [totalPages, setTotalPages] = useState<number>();
    const pageIndex = useRef<number>();
    const [totalCount, setTotalCount] = useState<number>(
        searchBranches?.length
    );
    const [currentSearchText, setCurrentSearchText] = useState<string>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [, setSelectedBranch] = useState<GetTaxpayerBranchDto>();
    const haveOnlyOneBranch = taxpayerBranchs?.length === 1;
    const en = !currentLanguage.isRtl;
    const labelId = 'BranchAccessLabel';
    const maxBranches = 10;

    const options: IChoiceGroupOption[] = [
        {
            key: BranchAccess.All,
            text: t('allBranches'),
            disabled: readOnly,
        },
        {
            key: BranchAccess.Custom,
            text: t('onlySpecified'),
            disabled: haveOnlyOneBranch || readOnly,
        },
    ];

    const isValidForm = () => {
        return (
            currentInvitation.allBranches ||
            (currentInvitation.restrictedBranch?.length ?? 0) > 0
        );
    };

    useEffect(() => {
        if (readOnly && !currentTaxpayerProfile) {
            setIsLoading(true);
            dispatchLoadTaxpayerProfile();
        }
    }, []);

    useEffect(() => {
        if (lastError) {
            setIsLoading(false);
        }
    }, [lastError]);

    useEffect(() => {
        if (currentTaxpayerProfile) {
            setIsLoading(false);
            setSelectedBranches(
                currentInvitation.restrictedBranch
                    ? taxpayerBranchs?.filter((x) =>
                          currentInvitation?.restrictedBranch?.includes(
                              x.branchNumber ?? -1
                          )
                      ) ?? []
                    : []
            );
        }
    }, [taxpayerBranchs]);

    useEffect(() => {
        if (
            InviteUserWizard?.state.selectedTabKey &&
            (mode === 'Edit' ||
                InviteUserWizard?.state.selectedTabKey ===
                    inviteUserWizardKey.Branches)
        ) {
            InviteUserWizard.ChangeTabState(
                inviteUserWizardKey.Branches,
                isValidForm() ? 'completed' : 'error'
            );
        }
    }, [
        InviteUserWizard?.state.selectedTabKey,
        currentInvitation.allBranches,
        currentInvitation.restrictedBranch?.length,
    ]);

    useEffect(() => {
        if (!readOnly)
            dispatchInvitationRequest({
                type: 'updateRepresentation',
                payload: {
                    allBranches: selectedKey === BranchAccess.All,
                    restrictedBranch:
                        selectedBranches?.map((x) => x.branchNumber ?? -1) ??
                        [],
                },
            });
    }, [selectedBranches.length]);

    const onChangeSearchBranches = (
        branches: GetTaxpayerBranchDto[],
        pi?: number
    ) => {
        allBranches.current = branches;
        const branchIndex = (pi ?? 0) * nbItemPerPage;
        setSearchBranches(
            branches.slice(branchIndex, (branchIndex ?? 0) + nbItemPerPage)
        );
        setTotalCount(branches.length);
        setTotalPages(Math.ceil(branches.length / nbItemPerPage));
    };

    const onSearchBranch = (value: string | undefined, pi: number) => {
        if (pi !== pageIndex.current) {
            pageIndex.current = pi;
            onChangeSearchBranches(allBranches.current, pi);
            return;
        }
        setCurrentSearchText(value);
        const branches = (taxpayerBranchs ?? []).filter((Branch) => {
            if (
                selectedBranches?.findIndex(
                    (x) => x.branchID === Branch.branchID
                ) !== -1
            )
                return false;
            return (
                (Branch.namePrimaryLang ?? '').startsWith(value ?? '') ||
                (Branch.nameSecondaryLang ?? '').startsWith(value ?? '') ||
                (Branch.branchNumber ?? '').toString().startsWith(value ?? '')
            );
        });
        onChangeSearchBranches(branches, pi);
    };

    const removeBranch = (branchID?: number) => {
        setSelectedBranches(
            selectedBranches.filter((Branch) => {
                return Branch.branchID !== branchID;
            })
        );
    };

    const onChangeBranchAccess = (key: BranchAccess) => {
        setSelectedKey(key);
        setSelectedBranches([]);
        setSelectedBranch(undefined);
        setCurrentSearchText(undefined);
        pageIndex.current = 0;
        onChangeSearchBranches(taxpayerBranchs ?? []);
        dispatchInvitationRequest({
            type: 'updateRepresentation',
            payload: {
                allBranches: key === BranchAccess.All,
                restrictedBranch: undefined,
            },
        });
    };

    return (
        <div id="UserAccessBranches">
            <Label id={labelId}>
                <Stack horizontal verticalAlign="center">
                    <span>{t('brancheAccess')}&nbsp;&nbsp;</span>
                    <TooltipHost content={t('brancheAccess_ToolTip')}>
                        <Icon iconName="Info" />
                    </TooltipHost>
                </Stack>
            </Label>
            <ChoiceGroup
                selectedKey={selectedKey}
                options={options}
                onChange={(_, option) => {
                    onChangeBranchAccess(option?.key as BranchAccess);
                }}
                ariaLabelledBy={labelId}
            />

            {haveOnlyOneBranch ? (
                <MessageBar
                    messageBarType={MessageBarType.warning}
                    className="messageBar"
                    isMultiline={false}
                >
                    {t('brancheAccess_WarningMsg')}
                </MessageBar>
            ) : (
                <div
                    className={
                        selectedKey === BranchAccess.All
                            ? 'branchesSelector disable'
                            : 'branchesSelector'
                    }
                >
                    {!readOnly && (
                        <div className="branches-SearchBox">
                            <div className="headerSearch">
                                <Label>
                                    {t('branchesAllowed', {
                                        min: 1,
                                        max: maxBranches,
                                    })}
                                </Label>
                                <ActionButton
                                    onClick={() => setSelectedBranches([])}
                                >
                                    {t('removeAll')}
                                </ActionButton>
                            </div>
                            <div className="searchBoxContainer">
                                <SearchComboBox
                                    containerClassName={
                                        selectedBranches.length === maxBranches
                                            ? 'disable'
                                            : 'flexGrow'
                                    }
                                    alignment="Left"
                                    rtl={currentLanguage.isRtl}
                                    callOutWidth="Fixed"
                                    paginationSetting={{
                                        nbItemPerPage,
                                        nbPageShown: 5,
                                        currentPage: pageIndex.current,
                                        totalPage: totalPages ?? 0,
                                    }}
                                    items={searchBranches}
                                    totalCount={totalCount}
                                    accessKeyProp={(i: GetTaxpayerBranchDto) =>
                                        i?.branchNumber?.toString() ?? '-1'
                                    }
                                    // clearOnSelectedItem
                                    accessTextProp={(
                                        i: GetTaxpayerBranchDto
                                    ) => {
                                        return (
                                            currentSearchText ||
                                            (i.branchNumber?.toString() ?? '')
                                        );
                                    }}
                                    onChangeTextSearch={(text: string) => {
                                        return text;
                                    }}
                                    onSearch={(text: string | undefined,criterias: SearchCriteria[] | undefined,pi: number
                                    ) => {
                                        onSearchBranch(text, pi);
                                    }}
                                    onSelectItem={(newBranch) => {
                                        setSelectedBranch(newBranch);
                                        if (newBranch) {
                                            pageIndex.current =0;
                                            setSelectedBranches([
                                                ...selectedBranches,
                                                newBranch,
                                            ]);
                                            onChangeSearchBranches(
                                                allBranches?.current?.filter(
                                                    (e) =>
                                                        e.branchID !==
                                                        newBranch.branchID
                                                ) ?? []
                                            );
                                        }
                                    }}
                                    onRenderItem={(
                                        record: GetTaxpayerBranchDto
                                    ) => {
                                        return (
                                            <BranchSearchItem
                                                BranchLine={record}
                                                currentLanguage={
                                                    currentLanguage
                                                }
                                                align="Left"
                                            />
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    <div
                        className={
                            readOnly
                                ? 'branches-LineDetails margin_Top'
                                : 'branches-LineDetails'
                        }
                    >
                        {!isLoading &&
                            selectedBranches?.map((Branche) => {
                                return (
                                    <div>
                                        <div className="branchLine">
                                            <div className="branchDetails">
                                                <div>
                                                    <span className="branchNumber">
                                                        {Branche.branchNumber}
                                                    </span>
                                                    {` - ${
                                                        (en
                                                            ? Branche.namePrimaryLang ??
                                                              Branche.nameSecondaryLang
                                                            : Branche.nameSecondaryLang) ??
                                                        Branche.sourceAddress
                                                    }`}
                                                </div>
                                                <div className="address">
                                                    {en
                                                        ? Branche.address
                                                              ?.formattedAddressEn
                                                        : Branche.address
                                                              ?.formattedAddressAr}
                                                </div>
                                            </div>
                                            {!readOnly && (
                                                <div className="btnDelete">
                                                    <IconButton
                                                        iconProps={{
                                                            iconName: 'Delete',
                                                        }}
                                                        onClick={() => {
                                                            removeBranch(
                                                                Branche.branchID
                                                            );
                                                            onChangeSearchBranches(
                                                                [
                                                                    ...allBranches.current,
                                                                    Branche,
                                                                ]
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <hr />
                                    </div>
                                );
                            })}
                    </div>
                    {isLoading && (
                        <div className="loadingPanel">
                            <LoadingSpinner
                                messageLabel={t('common:loading')}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default connector(AccessBranches);
